<template>
  <v-dialog v-model="showDialog" width="30rem" transition="dialog-top-transition" class="mr-n10">
    <template #activator="{ props: activatorProps }">
      <slot name="activator" v-bind="activatorProps">
        <v-list-item @click="showSearchDialog = true" variant="text" v-bind="activatorProps" nav>
          <template #prepend>
            <v-icon>mdi-magnify</v-icon>
          </template>
          <v-list-item-title class="appearing-text" :class="{ show: hoveringNavCard }">
            {{ $t('basicActions.search') }}
          </v-list-item-title>
        </v-list-item>
      </slot>
    </template>
    <template #default="{ isActive }">
      <v-card v-bind="isActive" max-height="50vh">
        <v-row no-gutters align="center" justify="start" class="pa-0 ma-0">
          <v-col cols="auto">
            <v-text-field
              :placeholder="$t('search.placeholders.default')"
              v-model="search"
              hide-details
              prepend-inner-icon="mdi-magnify"
              autofocus
              style="width: 30rem"
              density="default"
            >
            </v-text-field>

            <v-card class="">
              <v-list>
                <v-list-item v-if="loadingOptions" class="text-center">
                  <v-progress-circular indeterminate size="15" width="1" />
                </v-list-item>

                <template v-else-if="hasOptions">
                  <v-list-item
                    v-for="option in remainingOptions"
                    :key="option.master_account_id"
                    :prepend-avatar="option.logo_url || 'mdi-building'"
                    @click="routeToAccount(option)"
                  >
                    <v-list-item-title>{{ option.account_name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ option.website }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>

                <v-list-item v-else>
                  <v-list-item-title>{{ noResultsMessage }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import algoliasearch from 'algoliasearch/lite'
import { log } from '@/plugins/logger'

export default {
  name: 'SearchDialog',
  props: {
    hoveringNavCard: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const searchClient = algoliasearch(
      import.meta.env.VITE_ALGOLIA_APP_ID,
      import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY
    )
    return {
      searchClient
    }
  },
  data() {
    return {
      search: '',
      options: [],
      addedAccounts: [],
      searchTimeout: null,
      showSearch: true,
      showDialog: false,
      showMenu: false,
      loadingOptions: false,
      autoCompleteIndexMap: {
        master_accounts: {
          indexName: 'master_accounts',
          params: {
            hitsPerPage: 10,
            restrictSearchableAttributes: ['account_name']
          }
        }
      }
    }
  },
  computed: {
    remainingOptions() {
      return this.options.filter(option => !this.addedAccounts.includes(option.master_account_id))
    },
    hasOptions() {
      return this.remainingOptions.length > 0
    },
    noResultsMessage() {
      return this.hasSearched
        ? this.$t('search.noResults')
        : this.$t('search.searchToBegin') + '...'
    },
    hasSearched() {
      return !this.loadingOptions && this.remainingOptions.length === 0 && this.search.length > 0
    }
  },
  watch: {
    search(newVal, oldVal) {
      if (!oldVal) {
        this.timeoutAlgoliaSearch()
      } else if (newVal && newVal.length > oldVal.length) {
        this.timeoutAlgoliaSearch()
      } else if (!newVal) {
        this.options.splice(0)
      }
    },
    showMenu(newVal) {
      if (!newVal && (!this.search || this.search.length < 1)) {
        this.options.splice(0)
      }
    }
  },

  methods: {
    handleBlur() {
      setTimeout(() => {
        this.showMenu = false
      }, 200)
    },

    timeoutAlgoliaSearch() {
      // debounce search
      this.loadingOptions = true
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.searchAlgolia()
      }, 500)
    },

    async searchAlgolia() {
      this.loadingOptions = true

      let index = this.searchClient.initIndex('master_accounts')
      let params = {
        hitsPerPage: 15
      }
      await index.search(this.search, params).then(hits => {
        this.options = hits.hits.filter(
          (item, index, self) => index === self.findIndex(t => t.website === item.website)
        )
        this.loadingOptions = false
      })
    },

    routeToAccount(account) {
      this.$router.push(`/accounts/${account.master_account_id}`)
      this.options.splice(0)
      this.showMenu = false
      this.search = ''
      this.showDialog = false
    }
  },
  mounted() {
    // set listener for 'cmd+k' to open search
    window.addEventListener('keydown', e => {
      if (e.key === 'k' && e.metaKey) {
        this.showDialog = true
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.autocompleteList {
  position: absolute;
  z-index: 110;
  width: 20rem;
}
.appearing-text {
  display: none;
  opacity: 0;

  &.show {
    display: block;
    animation: fadeIn 0.2s ease-in-out 0.1s forwards;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
